import { graphql } from "gatsby"
import React, { useMemo, useState } from "react"
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap"
import { BlogLink, PageLayout, PageTitle } from "../components"
import { SEO, Utils } from "../utils"

export default ({ data }) => {
  const [state, setState] = useState({
    query: "",
    selectedTag: "all",
    currentPage: 1,
    mobileFiltersOpen: false,
  })

  const POSTS_PER_PAGE = 6

  const allFeaturedImages = data.allFile.edges || []
  const allPosts = data.allMarkdownRemark.edges || []
  const regex = /\/[blog].*\/|$/
  const featuredImageMap = Utils.getImageMap(allFeaturedImages, regex)

  // Extract all unique tags with post counts
  const allTags = useMemo(() => {
    const tagCounts = {}
    allPosts.forEach(({ node }) => {
      if (node.frontmatter.tags) {
        node.frontmatter.tags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1
        })
      }
    })
    return Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([tag, count]) => ({ tag, count }))
  }, [allPosts])

  // Filter posts by search query and tag
  const filteredPosts = useMemo(() => {
    return allPosts.filter(({ node }) => {
      const matchesSearch = state.query === "" || 
        node.frontmatter.title.toLowerCase().includes(state.query.toLowerCase()) ||
        node.excerpt.toLowerCase().includes(state.query.toLowerCase()) ||
        (node.frontmatter.tags && node.frontmatter.tags.some(tag => 
          tag.toLowerCase().includes(state.query.toLowerCase())
        ))

      const matchesTag = state.selectedTag === "all" || 
        (node.frontmatter.tags && node.frontmatter.tags.includes(state.selectedTag))

      return matchesSearch && matchesTag
    })
  }, [allPosts, state.query, state.selectedTag])

  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE)
  const startIndex = (state.currentPage - 1) * POSTS_PER_PAGE
  const paginatedPosts = filteredPosts.slice(startIndex, startIndex + POSTS_PER_PAGE)

  // Handle search
  const handleSearch = (e) => {
    setState({
      ...state,
      query: e.target.value,
      currentPage: 1,
    })
  }

  // Handle tag filter
  const handleTagChange = (tag) => {
    setState({
      ...state,
      selectedTag: tag,
      currentPage: 1,
      mobileFiltersOpen: false,
    })
  }

  // Clear all filters
  const handleClearFilters = () => {
    setState({
      ...state,
      query: "",
      selectedTag: "all",
      currentPage: 1,
    })
  }

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setState({
      ...state,
      currentPage: pageNumber,
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = []
    const maxVisible = 5
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (state.currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i)
        pages.push('...')
        pages.push(totalPages)
      } else if (state.currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('...')
        pages.push(state.currentPage - 1)
        pages.push(state.currentPage)
        pages.push(state.currentPage + 1)
        pages.push('...')
        pages.push(totalPages)
      }
    }
    
    return pages
  }

  // Filter sidebar component
  const FilterSidebar = () => (
    <Card className="shadow-sm sticky-top" style={{ top: '20px' }}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Filters</h5>
          {(state.query !== "" || state.selectedTag !== "all") && (
            <Button 
              variant="link" 
              size="sm" 
              onClick={handleClearFilters}
              className="text-decoration-none p-0"
            >
              Clear all
            </Button>
          )}
        </div>

        {/* Search */}
        <Form.Group className="mb-4">
          <Form.Label className="fw-bold small text-muted">SEARCH</Form.Label>
          <Form.Control
            type="text"
            placeholder="Search posts..."
            value={state.query}
            onChange={handleSearch}
            size="sm"
          />
        </Form.Group>

        {/* Tags */}
        <div>
          <Form.Label className="fw-bold small text-muted mb-3">TAGS</Form.Label>
          <div className="d-flex flex-column gap-2">
            <Form.Check
              type="radio"
              id="tag-all"
              label={
                <span>
                  All Posts <span className="text-muted">({allPosts.length})</span>
                </span>
              }
              checked={state.selectedTag === "all"}
              onChange={() => handleTagChange("all")}
            />
            {allTags.map(({ tag, count }) => (
              <Form.Check
                key={tag}
                type="radio"
                id={`tag-${tag}`}
                label={
                  <span>
                    {tag} <span className="text-muted">({count})</span>
                  </span>
                }
                checked={state.selectedTag === tag}
                onChange={() => handleTagChange(tag)}
              />
            ))}
          </div>
        </div>
      </Card.Body>
    </Card>
  )

  return (
    <PageLayout pageName="blog">
      <SEO title="Blog" />
      <PageTitle title="Blog" />
      
      <Container className="py-4">
        <Row>
          {/* Mobile Filter Toggle */}
          <Col xs={12} className="d-lg-none mb-3">
            <Button
              variant="outline-primary"
              className="w-100"
              onClick={() => setState({ ...state, mobileFiltersOpen: !state.mobileFiltersOpen })}
            >
              {state.mobileFiltersOpen ? '✕ Close Filters' : '☰ Show Filters'}
            </Button>
          </Col>

          {/* Sidebar - Desktop Always Visible, Mobile Collapsible */}
          <Col 
            lg={3} 
            className={`mb-4 ${state.mobileFiltersOpen ? 'd-block' : 'd-none d-lg-block'}`}
          >
            <FilterSidebar />
          </Col>

          {/* Main Content */}
          <Col lg={9}>
            {/* Results Count */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <p className="text-muted mb-0">
                {filteredPosts.length === allPosts.length ? (
                  <span><strong>{filteredPosts.length}</strong> posts</span>
                ) : (
                  <span>
                    Showing <strong>{filteredPosts.length}</strong> of <strong>{allPosts.length}</strong> posts
                  </span>
                )}
              </p>
              <p className="text-muted mb-0 d-none d-md-block">
                Page {state.currentPage} of {totalPages || 1}
              </p>
            </div>

            {/* Blog Posts */}
            {paginatedPosts.length > 0 ? (
              <div>
                {paginatedPosts.map(({ node }) => (
                  <div key={node.id} className="mb-4">
                    <BlogLink
                      date={node.frontmatter.date}
                      to={node.fields.slug}
                      featuredImage={featuredImageMap[node.fields.slug]}
                      title={node.frontmatter.title}
                      subtitle={node.frontmatter.date}
                      excerpt={node.excerpt}
                      tags={node.frontmatter.tags}
                      time={node.frontmatter.read_time}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <Card className="text-center p-5 shadow-sm">
                <Card.Body>
                  <h5>No posts found</h5>
                  <p className="text-muted mb-3">
                    Try adjusting your search or filter
                  </p>
                  <Button variant="primary" onClick={handleClearFilters}>
                    Clear Filters
                  </Button>
                </Card.Body>
              </Card>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="mt-5" aria-label="Blog pagination">
                <ul className="pagination justify-content-center flex-wrap">
                  <li className={`page-item ${state.currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(state.currentPage - 1)}
                      disabled={state.currentPage === 1}
                      aria-label="Previous page"
                    >
                      Previous
                    </button>
                  </li>
                  
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <li key={`ellipsis-${index}`} className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    ) : (
                      <li 
                        key={page} 
                        className={`page-item ${state.currentPage === page ? 'active' : ''}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    )
                  ))}
                  
                  <li className={`page-item ${state.currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(state.currentPage + 1)}
                      disabled={state.currentPage === totalPages}
                      aria-label="Next page"
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        @media (max-width: 991.98px) {
          .sticky-top {
            position: relative !important;
            top: 0 !important;
          }
        }
        
        .page-link {
          cursor: pointer;
        }
        
        .pagination {
          gap: 0.25rem;
        }
        
        @media (max-width: 575.98px) {
          .pagination .page-item {
            margin-bottom: 0.25rem;
          }
        }
      `}</style>
    </PageLayout>
  )
}

export const query = graphql`
  query {
    allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/blog/" } }
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      totalCount
      edges {
        node {
          id
          frontmatter {
            title
            description
            tags
            author
            date(formatString: "DD MMMM, YYYY")
            read_time
          }
          fields {
            slug
          }
          excerpt
        }
      }
    }
    allFile(
      filter: {
        extension: { eq: "jpg" }
        relativePath: { regex: "/feature/" }
        relativeDirectory: { regex: "/content/blog/" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            fluid(maxWidth: 400) {
              ...GatsbyImageSharpFluid
            }
          }
          relativePath
        }
      }
    }
  }
`